import { useState } from "react"
import { RequestHistoryView } from "../../../assets/common-svg/CommonSvg";
import {
  Box,
  Chip,
  Divider,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { setOperationTitle } from "../../../store/reducers/operation";
import { useSocketInstance } from "../../../context/SocketContext";
import ConfirmationModal from "../../../components/modal-contents/ConfirmationModal";
import bmrSubjectApi from "../../services/bmrSubjectService";
import { notify } from "../../utils/methods/notification";
import { useDispatch } from "react-redux";
import CustomDrawer from "../../../components/custom-drawer/CustomDrawer";
import HeaderBackNav from "../../../components/header-back-navigation/HeaderBackNav";
import Timeline from "@mui/lab/Timeline";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import { dateFormater } from "../../utils/common/utils";
import moment from "moment";
import { useSelector } from "react-redux";
import { hasCRPermission } from '../../../utils/utils';
import FilePreviewModal from "../../components/common/FilePreview";
import DownloadImageApi from "../../../services/downloadImageService";
import { EyeIconButton } from "../../../assets/common-svg/CommonSvg";
import { clearActiveTaskSlug } from "../../slices/help";
import LoadingButton from "@mui/lab/LoadingButton";


const RequestHistory = ({ SubjectData, onUpdate }) => {
  const { socketInstance } = useSocketInstance();
  const [changeAppliedId, setChangeAppliedId] = useState("");
  const [qrId, setQrId] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [confimationTitle, setConfimationTitle] = useState("");
  const [openConfirmChangesApplied, setOpenConfirmChangesApplied] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [filePreview, setFilePreview] = useState("");
  const LoginData = useSelector((state) => state.loginData);
  const role_id = LoginData?.role_id;
  const crPermissions = useSelector((state) => state.loginData?.cr_permissions);

  const dispatch = useDispatch();
  const [isApproving, setIsApproving] = useState(null);

  const getAttachmentItems = (value) => {
    if (value === null || value === undefined || value === "") return [];

    const mapSingleValue = (item) => {
      if (item === null || item === undefined || item === "") return null;

      if (typeof item === "number") {
        return { id: item, label: `Attachment ${item}` };
      }

      if (typeof item === "string") {
        const trimmed = item.trim();
        if (!trimmed) return null;
        if (/^https?:\/\//i.test(trimmed)) {
          return { url: trimmed, label: "View Attachment" };
        }
        if (/^\d+$/.test(trimmed)) {
          return { id: Number(trimmed), label: `Attachment ${trimmed}` };
        }
        return { label: trimmed, fileName: trimmed };
      }

      if (typeof item === "object") {
        const mediaId = item?.media_id || item?.id;
        if (mediaId) {
          return {
            id: mediaId,
            label: item?.file_name || item?.file || `Attachment ${mediaId}`,
            fileName: item?.file_name || item?.file || "",
          };
        }
        if (item?.url) {
          return {
            url: item.url,
            label: item?.file_name || item?.file || "View Attachment",
            fileName: item?.file_name || item?.file || "",
          };
        }
      }

      return null;
    };

    if (Array.isArray(value)) {
      return value.map(mapSingleValue).filter(Boolean);
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];
      if ((trimmed.startsWith("[") && trimmed.endsWith("]")) || (trimmed.startsWith("{") && trimmed.endsWith("}"))) {
        try {
          const parsed = JSON.parse(trimmed);
          return getAttachmentItems(parsed);
        } catch (error) {
          // Fall back to treating value as a simple string
        }
      }
      if (trimmed.includes(",")) {
        return trimmed
          .split(",")
          .map((item) => mapSingleValue(item.trim()))
          .filter(Boolean);
      }
    }

    const mapped = mapSingleValue(value);
    return mapped ? [mapped] : [];
  };

  const handleAttachmentPreview = async (attachment) => {
    try {
      if (attachment?.url) {
        setFilePreview(attachment.url);
        setPreviewOpen(true);
        return;
      }
      if (!attachment?.fileName && !attachment?.id) {
        notify.error("Attachment is not available");
        return;
      }
      const imgRes = attachment?.fileName
        ? await DownloadImageApi.getDownloadedImageUrlByName(attachment.fileName)
        : await DownloadImageApi.getDownloadedImageul(attachment.id);
      const response = await fetch(imgRes?.data || "");
      if (!response.ok) {
        throw new Error("Attachment could not be loaded");
      }
      setFilePreview(response.url);
      setPreviewOpen(true);
    } catch (error) {
      notify.error(error?.message || "Failed to load attachment");
    }
  };

  const longTextSx = {
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    maxWidth: "100%",
  };

  const isDateString = (value) => {
    if (typeof value !== 'string') return false;
    // Matches common date formats: YYYY-MM-DD, DD/MM/YYYY, MM-DD-YYYY, ISO 8601, etc.
    const dateRegex = /(\d{4}-\d{2}-\d{2}|\d{2}[/-]\d{2}[/-]\d{4}|\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/;
    return dateRegex.test(value) && (!isNaN(Date.parse(value)) || moment(value, ["DD-MM-YYYY", "DD/MM/YYYY"], true).isValid());
  };

  const DisplayValue = ({ value, color = "warning.main" }) => {

    if (value === null || value === undefined) {
      return <Typography variant="body2" color={color}>--</Typography>;
    }

    switch (typeof value) {
      case 'string':
        return (
          <Typography variant="body2" color={color}>
            {isDateString(value) ? (dateFormater(value) || '--') : value}
          </Typography>
        );
      case 'number':
        return <Typography variant="body2" color={color}>{value.toString()}</Typography>;
      case 'boolean':
        return <Typography variant="body2" color={color}>{value ? 'Yes' : 'No'}</Typography>;
      case 'object':
        if (value === null) return <Typography variant="body2" color={color}>--</Typography>;
        return <Typography variant="body2" color={color}>{JSON.stringify(value)}</Typography>;
      default:
        return <Typography variant="body2" color={color}>--</Typography>;
    }
  };

  const KeyValueDisplay = ({ data, color = "warning.main" }) => {
    const handleRemoveUnderScore = (value) => {
      return value
        .split("_")
        .join(" ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    };
    if (!data || Object.keys(data).length === 0) {
      return <Typography variant="body2">--</Typography>;
    }

    return (
      <Box
        className="bg-white p-2 rounded border"
        sx={{ maxWidth: "100%", minWidth: 0, overflow: "hidden" }}
      >
        {Object.entries(data).map(([key, value]) => (
          <Box key={key} sx={{ maxWidth: "100%", minWidth: 0, mb: 0.5 }}>
            <Typography
              variant="body2"
              component="span"
              sx={{ fontWeight: "bold", wordBreak: "break-word" }}
            >
              {handleRemoveUnderScore(key)}:{" "}
            </Typography>
            {key?.toLowerCase() === "slot_attachment" ? (
              (() => {
                const attachments = getAttachmentItems(value);
                if (!attachments.length) {
                  return <Typography variant="body2" color={color}>--</Typography>;
                }
                return (
                  <Box sx={{ maxWidth: "100%", minWidth: 0 }}>
                    {attachments.map((attachment, index) => (
                      <Box
                        key={`${attachment?.id || attachment?.url || attachment?.label}-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          mb: 0.5,
                          minWidth: 0,
                          maxWidth: "100%",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color={color}
                          sx={{
                            ...longTextSx,
                            minWidth: 0,
                            flex: "1 1 auto",
                          }}
                        >
                          {attachment?.label || `Attachment ${index + 1}`}
                        </Typography>
                        <Tooltip title="View">
                          <Box component="span" sx={{ flexShrink: 0, lineHeight: 0 }}>
                            <EyeIconButton
                              bgColor={"#3571FF"}
                              handleOnClick={() => handleAttachmentPreview(attachment)}
                            />
                          </Box>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                );
              })()
            ) : (
              <DisplayValue value={value} color={color} />
            )}
          </Box>
        ))}
      </Box>
    );
  };
  // handle the confirmation Chnages Applied
  const handleConfirmationChnagesApplied = async (confirmationResult) => {
    if (confirmationResult) {
      let shouldRefreshPage = false;
      const payload = {
        request_reason_id: changeAppliedId,
        qr_code_id: qrId,
        confirm: true,
      };
      setIsApproving(changeAppliedId);
      try {
        const response = await bmrSubjectApi.requestApproval(payload);
        handleGetHistoryData();
        setHistoryOpen(!historyOpen);
        if (response.status == 200) {
          notify.success(response.message);
          shouldRefreshPage = true;
          if (onUpdate)
            onUpdate()

        }
      } catch (error) {
        notify.error(error?.message || error || "Failed to add appointment")
      } finally {
        setIsApproving(null);
        setOpenConfirmChangesApplied(false);
        if (shouldRefreshPage) {
          dispatch(clearActiveTaskSlug());
          dispatch(setOperationTitle(null));
          if (!onUpdate) {
            setTimeout(() => {
              window.location.reload();
            }, 150);
          }
        }
      }
    }
    //  else {
    //   console.log("No Choosed")
    //  }
  };

  const handleRequestApproval = async (id, qr_id) => {
    setIsApproving(id);
    setChangeAppliedId(id);
    setQrId(qr_id);
    const payload = { request_reason_id: id, qr_code_id: qr_id };
    try {
      const response = await bmrSubjectApi.requestApproval(payload);
      if (response.status == 200) {
        handleGetHistoryData();
        setHistoryOpen(!historyOpen);
        if (onUpdate)
          onUpdate()
        dispatch(clearActiveTaskSlug());
        dispatch(setOperationTitle(null));
        notify.success(response.message)
      }
      if (response.confirm) {
        if (socketInstance) {
          socketInstance.emit("count_task", null);
        }
        setConfimationTitle(response.message);
        await dispatch(setOperationTitle(null));
        setOpenConfirmChangesApplied(true);
      }
    } catch (error) {
      notify.error(error?.message || error || "Failed to add appointment")
    } finally {
      setIsApproving(null);
    }
  };
  // console.log("crPermissions",crPermissions)

  // useEffect(()=>{

  //   console.log("Request history data updated useeffect:", historyData);
  // },[])

  // let PrviousVal;
  const handleGetHistoryData = async () => {
    const payload = {
      subject_id: SubjectData.id,
      phase_id: SubjectData.phase_id,
      page: 1,
      limit: 10,
      feasibility_id: SubjectData?.feasibility_id || SubjectData?.feasibility_id || SubjectData?.feasibility_assessments?.id || ''
    };
    try {
      const response = await bmrSubjectApi.requestHistory(
        payload.limit,
        payload.page,
        payload.phase_id,
        payload.subject_id,
        {},
        payload.feasibility_id,
        undefined,
        undefined,
        true
      );
      // console.log("Request history API response:", response);

      if (response.data.length > 0) {
        setHistoryOpen(!historyOpen);
        setHistoryData(response.data);
      } else {
        notify.error("No CR history found")
      }
    } catch (error) {
      notify.error(error?.message || error || "Failed to add appointment")
    }
  };

  const handleCloseBar = () => {
    setHistoryOpen(!historyOpen);
  }
  // Handle the toggleDrawer for the history viewer:
  const handleHistoryDrawer = () => {
    // setHistoryOpen(!historyOpen);
    handleGetHistoryData();
  };



  return (<><Tooltip
    title="Request History"
    onClick={handleHistoryDrawer}
  >
    <button className="cursor-pointer">
      <RequestHistoryView />
    </button>
  </Tooltip>
    {/* Add Custom Drawer for the history viewer */}
    <CustomDrawer open={historyOpen} toggleDrawer={handleHistoryDrawer}>
      <div className="px-6 py-4 w-full">
        <HeaderBackNav
          handleBackNav={handleCloseBar}
          TitleFontSize={"20px"}
          TitleFontWeight={700}
          Title={"Request History"}
        />
      </div>
      <Divider />

      <div className="px-6 py-4">
        {historyData?.map((item, index) => (
          <Timeline
            key={index}
            className="!max-w-[40rem]"
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot
                  color={item.status == 1 ? "success" : "warning"}
                />
                {index !== historyData.length - 1 && (
                  <TimelineConnector sx={{ bgcolor: "grey.400" }} />
                )}
              </TimelineSeparator>
              <TimelineContent sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                <Chip
                  size="small"
                  color={item.status == 1 ? "success" : "warning"}
                  label={
                    item.createdAt
                      ? moment(item.createdAt).format("DD/MM/YYYY")
                      : "--"
                  }
                />

                <div className="bg-gray-50 flex flex-col gap-4 p-4 rounded-lg mt-3 shadow-sm min-w-0 max-w-full">
                  <div>
                    <Typography variant="h6">
                      {item?.changeRequestReason?.title || "-"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", ...longTextSx }}
                      className="capitalize mt-1"
                    >
                      {Array.isArray(item?.change_request_reason_value)
                        ? item?.change_request_reason_value
                            .map((res) => res.title)
                            .join(", ")
                        : "--"}
                    </Typography>
                  </div>

                  <Typography variant="body1" sx={longTextSx}>
                    {item.description}
                  </Typography>

                  <Grid container spacing={3} sx={{ minWidth: 0 }}>
                    {/* Previous Value */}
                    <Grid item xs={12} md={6} sx={{ minWidth: 0, maxWidth: "100%" }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Previous Value
                      </Typography>
                      {item.previous_value ? (
                        // dob  
                        <KeyValueDisplay
                         data={item.previous_value ? JSON.parse(item.previous_value) : {}}
                          color="warning.main"
                        />
                      ) : (
                        "--"
                      )}
                    </Grid>

                    {/* Updated Value */}
                    <Grid item xs={12} md={6} sx={{ minWidth: 0, maxWidth: "100%" }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        New Value
                      </Typography>
                      {item.updated_value ? (
                        <KeyValueDisplay
                          data={JSON.parse(item.updated_value)}
                          color="success.main"
                        />
                      ) : (
                        "--"
                      )}
                    </Grid>

                    {/* Resolver Info */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Resolved By
                      </Typography>
                      <Typography variant="body2" className="capitalize">
                        {item.resolver ? item.resolver.name : "--"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Resolved Date
                      </Typography>
                      <Typography variant="body2">
                        {item.resolver
                          ? moment(item.updatedAt).format("DD/MM/YYYY")
                          : "--"}
                      </Typography>
                    </Grid>
                  </Grid>
                  {!item.resolver && (role_id === 1 || hasCRPermission(crPermissions, [item?.change_request_reason_value?.[0]?.slug], "RESOLVE")) && (
                    <LoadingButton
                      variant="contained"
                      size="small"
                      type="button"
                      color="primary"
                      disabled={item.status != "1" || (isApproving && isApproving !== item.id)}
                      loading={isApproving === item.id}
                      onClick={() => handleRequestApproval(item.id, item?.qrCode?.id)}
                      sx={{ alignSelf: "start" }}
                    >
                      Change Applied
                    </LoadingButton>
                  )}
                </div>
              </TimelineContent>
            </TimelineItem>
          </Timeline>
        ))}
      </div>
    </CustomDrawer>
    {/******************seperate this code**********/}
    <ConfirmationModal
      open={openConfirmChangesApplied}
      setOpen={setOpenConfirmChangesApplied}
      errMessage={confimationTitle}
      handleConfirmation={handleConfirmationChnagesApplied}
    />
    <FilePreviewModal
      ModalTitle={"File Preview"}
      setOpen={setPreviewOpen}
      open={previewOpen}
      fileURL={filePreview}
    />
  </>)
}
export default RequestHistory